import React, { useState, useMemo, useEffect, useRef } from "react";
import { Header } from "../components/shared";
import { CheckoutFooter } from "../components/checkout";
import { FilterSidebar } from "../components/catalog";
import CamperCard from "../components/shared/CamperCard";
import AvailabilityEnquiry from "../components/catalog/AvailabilityEnquiry";
import LoadingSpinner from "../components/shared/LoadingSpinner";
import { useTranslation } from "../hooks/useTranslation";
import "../styles/catalog/VanCatalog.css";
import { useVehicles } from "../context/VehicleContext";
import { SEO, generateBreadcrumbSchema } from "../seo";

const VanCatalog = () => {
  const { t } = useTranslation('catalog');
  const { selectedDates } = useVehicles();
  const searchInProgress = useRef(false);

  // Filter state
  const [filters, setFilters] = useState({
    category: "all",
    features: {},
    travelers: "",  // No selection by default - show all vehicles
    beds: "",
    pickupDate: selectedDates.pickupDate || "",
    dropoffDate: selectedDates.dropoffDate || "",
  });

  // MOCK DATA: Commented out for API integration. Uncomment if needed for fallback.
  /*
  const productData = [
        {
      id: 1,
      title: "Nest Catcher",
      price: "$90.00",
      description:
        "Your family's adventure HQ! The Nest Catcher sleeps up to six, featuring bunk beds",
      features: ["3 Sleeping Areas", "Kitchen", "WIFI", "Solar-Powered"],
      capacity: "Up to 7 People",
      maxCapacity: 7,
      bedCount: 3,
      category: "family",
      image:
        "/assets/images/placeholders/campervan-placeholder.jpg`},
    },
    {
      id: 2,
      title: "Wheel Catcher",
      price: "$90.00",
      description:
        "Accessible freedom on the road! The Wheel Catcher makes van life inclusive, with ramp access, wider doors, and an adapted bathroom — all designed for comfort and ease of movement.",
      features: [
        "5 Sleeping Areas",
        "Kitchen",
        "WIFI",
        "Wheelchair-accessible ramp",
      ],
      capacity: "Up to 2 People",
      maxCapacity: 2,
      bedCount: 5,
      category: "accessibility",
      image:
        "/assets/images/placeholders/campervan-placeholder.jpg`},
    },
    {
      id: 3,
      title: "Sun Catcher",
      price: "$90.00",
      description:
        "A bright escape for two! The Sun Catcher is ideal for weekend getaways with its sunset-inspired interior, solar-powered setup, compact kitchen, and all essentials including bedding and a chemical toilet.",
      features: ["3 Sleeping Areas", "Kitchen", "WIFI", "Solar-Powered"],
      capacity: "Up to 3 People",
      maxCapacity: 3,
      bedCount: 3,
      category: "couple",
      image:
        "/assets/images/placeholders/campervan-placeholder.jpg`},
    },
    {
      id: 4,
      title: "Digital Catcher",
      price: "$90.00",
      description:
        "A bright escape for two! The Digital Catcher is ideal for weekend getaways with its sunset-inspired interior, solar-powered setup, compact kitchen, and all essentials including bedding and a chemical toilet.",
      features: ["5 Sleeping Areas", "Kitchen", "WIFI", "Work Space"],
      capacity: "Up to 5 People",
      maxCapacity: 5,
      bedCount: 5,
      category: "nomads",
      image:
        "/assets/images/placeholders/campervan-placeholder.jpg`},
    },
    {
      id: 5,
      title: "Wave Catcher",
      price: "$90.00",
      description:
        "Ride the waves in style! The Wave Catcher is tailored for surf lovers, with cool ocean tones, surfboard storage, and a compact outdoor shower for post-surf rinses.",
      features: ["3 Sleeping Areas", "Kitchen", "WIFI", "Solar-Powered"],
      capacity: "Up to 8 People",
      maxCapacity: 8,
      bedCount: 3,
      category: "surf",
      image:
        "/assets/images/placeholders/campervan-placeholder.jpg`},
    },
    {
      id: 6,
      title: "Forest Catcher",
      price: "$90.00",
      description:
        "A cozy cabin on wheels! The Forest Catcher is built for nature lovers and hikers, featuring warm woods, ample storage for gear, and a fold-out awning for outdoor dining.",
      features: [
        "5 Sleeping Areas",
        "Kitchen",
        "WIFI",
        "Wheelchair-accessible ramp",
      ],
      capacity: "Up to 5 People",
      maxCapacity: 5,
      bedCount: 5,
      category: "family",
      image:
        "/assets/images/placeholders/campervan-placeholder.jpg`},
    },
    {
      id: 7,
      title: "Tide Catcher",
      price: "$90.00",
      description:
        "Your rolling beach base! The Tide Catcher is made for coastal adventures with coastal-inspired interiors, outdoor shower, and gear storage for all your beach essentials.",
      features: ["3 Sleeping Areas", "Kitchen", "WIFI", "Solar-Powered"],
      capacity: "Up to 2 People",
      maxCapacity: 2,
      bedCount: 3,
      category: "surf",
      image:
        "/assets/images/placeholders/campervan-placeholder.jpg`},
    },
    {
      id: 8,
      title: "Soul Catcher",
      price: "$90.00",
      description:
        "A soulmatic retreat on wheels! The Soul Catcher is ideal for couples, with ambient lighting, soft fabrics, and a romantic setup perfect for intimate getaways.",
      features: [
        "5 Sleeping Areas",
        "Kitchen",
        "WIFI",
        "Wheelchair-accessible ramp",
      ],
      capacity: "Up to 2 People",
      maxCapacity: 2,
      bedCount: 5,
      category: "accessibility",
      image:
        "/assets/images/placeholders/campervan-placeholder.jpg`},
    },
  ];
  */

  // Get vehicles data from context
  const { vehicles, loading, error, searchVehiclesByDates, loadVehicles } = useVehicles();

  // Update filters when selectedDates change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      pickupDate: selectedDates.pickupDate || "",
      dropoffDate: selectedDates.dropoffDate || "",
    }));
  }, [selectedDates]);

  // Trigger API search when both dates are selected (with debouncing)
  useEffect(() => {
    if (!filters.pickupDate || !filters.dropoffDate || searchInProgress.current) return;

    const timeoutId = setTimeout(async () => {
      if (searchInProgress.current) return;
      searchInProgress.current = true;
      try {
        await searchVehiclesByDates(filters.pickupDate, filters.dropoffDate);
      } finally {
        searchInProgress.current = false;
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pickupDate, filters.dropoffDate]);

  // Filter logic
  const filteredProducts = useMemo(() => {
    // If API fails or data is not yet loaded, return empty array
    if (!vehicles || !Array.isArray(vehicles)) {
      return [];
    }

    return vehicles.filter((product) => {
      // Category filter - vehicles can have multiple categories
      if (filters.category !== "all") {
        // Check if vehicle has the selected category in either category or categories field
        const hasCategory = (cat) => {
          // Check single category field (backward compatibility)
          if (product.category && product.category.includes(cat)) {
            return true;
          }
          // Check categories array
          if (product.categories && Array.isArray(product.categories)) {
            return product.categories.includes(cat);
          }
          return false;
        };

        if (filters.category === "family" && !hasCategory("family"))
          return false;
        if (filters.category === "surf" && !hasCategory("surf"))
          return false;
        if (filters.category === "nomads" && !hasCategory("nomads"))
          return false;
      }

      // Features filter
      const selectedFeatures = Object.keys(filters.features).filter(
        (key) => filters.features[key],
      );
      if (selectedFeatures.length > 0) {
        const hasRequiredFeatures = selectedFeatures.every((feature) => {
          switch (feature) {
            case "solar":
              return product.features.some(f => f.text === "Solar-Powered");
            case "kitchen":
              return product.features.some(f => f.text === "Kitchen");
            case "wifi":
              return product.features.some(f => f.text === "WIFI");
            case "wheelchair":
              return product.features.some(f => f.text === "Wheelchair-accessible ramp");
            default:
              return false;
          }
        });
        if (!hasRequiredFeatures) return false;
      }

      // Travelers filter - only apply if a selection is made
      if (filters.travelers && filters.travelers !== "") {
        const maxCapacity = product.maxCapacity || product.max_capacity || 0;

        if (filters.travelers === "1-2") {
          // All vehicles can accommodate 1-2 people, so don't filter any out
          // Unless capacity is 0 or missing
          if (maxCapacity < 1) return false;
        }
        if (filters.travelers === "3-4") {
          // Show vehicles that can accommodate at least 3 people
          if (maxCapacity < 3) return false;
        }
        if (filters.travelers === "5+") {
          // Show only vehicles that can accommodate 5 or more people
          if (maxCapacity < 5) return false;
        }
      }

      // Beds filter removed - maximum of 2 beds makes this filter confusing

      return true;
    });
  }, [vehicles, filters]);

  // Filter handlers
  const handleCategoryChange = (category) => {
    setFilters((prev) => ({ ...prev, category }));
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const handleFeatureToggle = (feature, isChecked) => {
    setFilters((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: isChecked,
      },
    }));
  };

  const handleResetFilters = async () => {
    // Reset all filter states
    setFilters({
      category: "all",
      features: {},
      travelers: "",  // No selection - show all vehicles
      beds: "",       // No selection
      pickupDate: "",
      dropoffDate: "",
    });

    // Reload all vehicles (without date filtering)
    try {
      await loadVehicles(true); // Force reload all vehicles
    } catch (error) {
      console.error('Error reloading vehicles after reset:', error);
    }
  };

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', path: '' },
    { name: 'Catalog', path: '/catalog' },
  ]);

  return (
    <div className="van-catalog">
      <SEO schema={breadcrumbSchema} />
      <div className="catalog-container">
        <Header />

        <div className="catalog-content">
          <h1 className="page-title">{t('page.title')}</h1>

          <div className="filter-chips">
            {/* Category filter tabs */}
            <div className={`chip ${filters.category === "all" ? "active" : ""}`} onClick={() => handleCategoryChange("all")} style={{ cursor: "pointer" }}><div className="chip-content">{t('categories.all')}</div></div>
            <div className={`chip ${filters.category === "family" ? "active" : ""}`} onClick={() => handleCategoryChange("family")} style={{ cursor: "pointer" }}><div className="chip-content">{t('categories.family')}</div></div>
            <div className={`chip ${filters.category === "surf" ? "active" : ""}`} onClick={() => handleCategoryChange("surf")} style={{ cursor: "pointer" }}><div className="chip-content">{t('categories.surf')}</div></div>
            <div className={`chip ${filters.category === "nomads" ? "active" : ""}`} onClick={() => handleCategoryChange("nomads")} style={{ cursor: "pointer" }}><div className="chip-content">{t('categories.nomads')}</div></div>
          </div>
        </div>

        <div className="main-content">
          <FilterSidebar
            filters={filters}
            onFilterChange={handleFilterChange}
            onFeatureToggle={handleFeatureToggle}
            onResetFilters={handleResetFilters}
          />
          <div className="products-section">
            {loading ? (
              <LoadingSpinner text={t('page.loading')} />
            ) : error ? (
              <div style={{ color: 'red' }}>{t('page.error')}: {error}</div>
            ) : (
              <>
                <div className="product-grid">
                  {filteredProducts.map((product) => (
                    <CamperCard key={product.id} vehicle={product} variant="catalog" />
                  ))}
                </div>
                {filteredProducts.length === 0 ? (
                  /* An empty result is a lead, not a dead end: slots are not all
                     online, so offer to check rather than showing nothing. */
                  <AvailabilityEnquiry
                    pickupDate={filters.pickupDate}
                    dropoffDate={filters.dropoffDate}
                  />
                ) : (
                  <div className="load-more-container">
                    <button className="load-more-btn">{t('page.loadMore')}</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <CheckoutFooter />
      </div>
    </div>
  );
};

export default VanCatalog;
